import { Box, Divider, Slider, Stack, Typography } from "@mui/material";
import React from "react";
import cardBackground from "../../../../../../assets/card-background.jpg";
import cardBorder from "../../../../../../assets/card-border.jpg";
import cardShadow from "../../../../../../assets/card-shadow.jpg";
import cardTransparency from "../../../../../../assets/card-transparency.jpg";
import completedProgress from "../../../../../../assets/completed-progress.jpg";
import contentTypeColor from "../../../../../../assets/content-type-color.jpg";
import inProgressProgress from "../../../../../../assets/in-progress-progress.jpg";
import notStartedProgress from "../../../../../../assets/not-started-progress.jpg";
import ColorPicker from "../../../../../../components/ColorPicker/ColorPicker";
import {
  PageUid,
  useCustomisation,
} from "../../../../../../context/CustomisationContext";
import { usePortal } from "../../../../../../context/PortalsContext";
import { updateSectionDesign } from "../../../../../../services/api/apiClient";
import { brand, error, gray } from "../../../../../../theme/themePrimitives";

interface CardsCustomisationProps {
  pageId?: PageUid;
}
const formattedDate = new Date()
  .toLocaleDateString("en-GB")
  .replace(/\//g, ".");

const borderSizes = ["N/A", "2px", "4px", "8px"];
const shadowSizes = ["N/A", "XS", "SM", "MD", "LG"];

export const CardsCustomisation: React.FC<CardsCustomisationProps> = ({
  pageId = "HOME",
}) => {
  const { updateCustomisationSection, getCustomisationValue } =
    useCustomisation();
  const { portals } = usePortal();

  const handleAttributeChange = (
    attributeKey: string,
    newAttributeValue?: string,
  ) => {
    if (newAttributeValue === undefined) return;

    updateCustomisationSection(pageId, "cardsSettings", {
      [attributeKey]: newAttributeValue,
    });

    updateSectionDesign(
      portals.selectedPortal?.portalUid!,
      pageId,
      "cardsSettings",
      {
        attributeKey,
        attributeValue: newAttributeValue,
      },
    );
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5" fontWeight="bold">
          Cards
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Customize the appearance of assignment cards.
        </Typography>
      </Box>
      <Divider />
      {/* Borders */}
      <Stack spacing={1}>
        <Typography fontSize={"16px"} fontWeight="bold">
          Borders
        </Typography>
        <Box
          sx={{
            border: `1px solid ${gray[300]}`,
            borderRadius: "8px",
            px: 2,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              py: 2,
              gap: 2,
            }}
          >
            <Stack spacing={6}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <Typography>Border Color</Typography>
                <ColorPicker
                  value={
                    getCustomisationValue(
                      pageId,
                      "cardsSettings",
                      "cardBorderColor",
                    ) ?? ""
                  }
                  onChange={(color) =>
                    handleAttributeChange("cardBorderColor", color)
                  }
                />
              </Box>
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography>Border size</Typography>
                  <Typography
                    sx={{ cursor: "pointer" }}
                    color={brand[600]}
                    onClick={() => {
                      handleAttributeChange("cardBorderSize", "");
                      handleAttributeChange("cardBorderColor", "");
                    }}
                  >
                    Clear
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr 1fr",
                    gap: 2,
                  }}
                >
                  {borderSizes.map((value) => {
                    const isSelected =
                      value === "N/A"
                        ? getCustomisationValue(
                            pageId,
                            "cardsSettings",
                            "cardBorderSize",
                          ) === "0px"
                        : getCustomisationValue(
                            pageId,
                            "cardsSettings",
                            "cardBorderSize",
                          ) === value;

                    return (
                      <Box
                        key={value}
                        onClick={() =>
                          handleAttributeChange(
                            "cardBorderSize",
                            value === "N/A" ? "0px" : value,
                          )
                        }
                        sx={{
                          cursor: "pointer",
                          border: `${value === "N/A" ? "1px" : value} solid ${
                            isSelected ? brand[600] : gray[300]
                          }`,
                          boxSizing: "border-box",
                          height: "50px",
                          borderRadius: "12px",
                          padding: "8px 16px",
                          textAlign: "center",
                          color: isSelected ? brand[600] : "inherit",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "border 0.3s ease, color 0.3s ease",
                        }}
                      >
                        <Typography variant="body2">{value}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Stack>
            <Box
              sx={{ width: "100%", height: "200px" }}
              component={"img"}
              src={cardBorder}
              alt="card-border"
            />
          </Box>
        </Box>
      </Stack>
      {/* Shadows */}
      <Stack spacing={1}>
        <Typography fontSize={"16px"} fontWeight="bold">
          Shadows
        </Typography>
        <Box
          sx={{
            border: `1px solid ${gray[300]}`,
            borderRadius: "8px",
            px: 2,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              py: 2,
              gap: 2,
            }}
          >
            <Stack spacing={6}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <Typography>Shadow Color</Typography>
                <ColorPicker
                  value={
                    getCustomisationValue(
                      pageId,
                      "cardsSettings",
                      "cardShadowColor",
                    ) ?? ""
                  }
                  onChange={(color) =>
                    handleAttributeChange("cardShadowColor", color)
                  }
                />
              </Box>
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography>Shadow size</Typography>
                  <Typography
                    sx={{ cursor: "pointer" }}
                    color={brand[600]}
                    onClick={() => {
                      handleAttributeChange("cardShadowSize", "");
                      handleAttributeChange("cardShadowColor", "");
                    }}
                  >
                    Clear
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr",
                    gap: 2,
                  }}
                >
                  {shadowSizes.map((s) => {
                    const isSelected =
                      getCustomisationValue(
                        pageId,
                        "cardsSettings",
                        "cardShadowSize",
                      ) === s;

                    return (
                      <Box
                        key={s}
                        onClick={() =>
                          handleAttributeChange("cardShadowSize", s)
                        }
                        sx={{
                          cursor: "pointer",
                          border: `1px solid ${
                            isSelected ? brand[600] : gray[300]
                          }`,
                          height: "50px",
                          borderRadius: "12px",
                          padding: "8px 16px",
                          textAlign: "center",
                          color: isSelected ? brand[600] : "inherit",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "border 0.3s ease, color 0.3s ease",
                        }}
                      >
                        <Typography variant="body2">{s}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Stack>
            <Box
              sx={{ width: "100%", height: "200px" }}
              component={"img"}
              src={cardShadow}
              alt="card-shadow"
            />
          </Box>
        </Box>
      </Stack>
      {/* Background */}
      <Stack spacing={1}>
        <Typography fontSize={"16px"} fontWeight="bold">
          Background
        </Typography>
        <Box
          sx={{
            border: `1px solid ${gray[300]}`,
            borderRadius: "8px",
            px: 2,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              py: 2,
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <Typography>Card background colour</Typography>
              <ColorPicker
                value={
                  getCustomisationValue(
                    pageId,
                    "cardsSettings",
                    "cardBackgroundColor",
                  ) ?? ""
                }
                onChange={(color) =>
                  handleAttributeChange("cardBackgroundColor", color)
                }
              />
            </Box>
            <Box
              sx={{ width: "100%", height: "200px" }}
              component={"img"}
              src={cardBackground}
              alt="card-background"
            />
          </Box>
        </Box>
        <Box
          sx={{
            border: `1px solid ${gray[300]}`,
            borderRadius: "8px",
            px: 2,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              py: 2,
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <Typography>Thumbnail transparency</Typography>
              <Slider
                value={
                  (getCustomisationValue(
                    pageId,
                    "cardsSettings",
                    "cardTransparencyLevel",
                  ) ?? "0") as unknown as number
                }
                onChangeCommitted={(event, newValue) =>
                  handleAttributeChange(
                    "cardTransparencyLevel",
                    newValue.toString(),
                  )
                }
                step={1}
                min={0}
                max={100}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${v}%`}
                sx={{
                  color: brand[600],
                  "& .MuiSlider-thumb": {
                    backgroundColor: "white",
                    border: "2px solid",
                    borderColor: brand[600],
                    boxShadow: "none",
                    "&:hover": {
                      boxShadow: "0px 0px 0px 6px rgba(0, 0, 0, 0.1)",
                    },
                    "&.Mui-active": {
                      boxShadow: "0px 0px 0px 10px rgba(0, 0, 0, 0.15)",
                    },
                  },
                  "& .MuiSlider-track": {
                    backgroundColor: brand[600],
                  },
                  "& .MuiSlider-rail": {
                    backgroundColor: "#e0e0e0",
                  },
                }}
              />
            </Box>
            <Box
              sx={{ width: "100%", height: "200px" }}
              component={"img"}
              src={cardTransparency}
              alt="card-transparency"
            />
          </Box>
        </Box>
      </Stack>
      {/* Progress bar */}
      <Stack spacing={1}>
        <Typography fontSize={"16px"} fontWeight="bold">
          Progress bar
        </Typography>
        <Stack
          sx={{
            border: `1px solid ${gray[300]}`,
            borderRadius: "8px",
            px: 2,
          }}
        >
          <Box py={2}>
            <Typography fontSize={"14px"}>
              Not started
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",

                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <Typography>Progress bar colour</Typography>
                <ColorPicker
                  value={
                    getCustomisationValue(
                      pageId,
                      "cardsSettings",
                      "cardNotStartedProgressBarColor",
                    ) ?? ""
                  }
                  onChange={(color) =>
                    handleAttributeChange(
                      "cardNotStartedProgressBarColor",
                      color,
                    )
                  }
                />
              </Box>
              <Box
                sx={{ width: "100%", height: "200px" }}
                component={"img"}
                src={notStartedProgress}
                alt="not-started-progress"
              />
            </Box>
          </Box>
          <Divider />
          <Box mt={1}>
            <Typography fontSize={"14px"}>
              In progress
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                py: 2,
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <Typography>In progress bar colour</Typography>
                <ColorPicker
                  value={
                    getCustomisationValue(
                      pageId,
                      "cardsSettings",
                      "cardInProgressBarColor",
                    ) ?? ""
                  }
                  onChange={(color) =>
                    handleAttributeChange("cardInProgressBarColor", color)
                  }
                />
              </Box>
              <Box
                sx={{ width: "100%", height: "200px" }}
                component={"img"}
                src={inProgressProgress}
                alt="in-progress-progress"
              />
            </Box>
          </Box>
          <Divider />
          <Box mt={1}>
            <Typography fontSize={"14px"}>
              Completed
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                py: 2,
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <Typography>Completed progress bar colour</Typography>
                <ColorPicker
                  value={
                    getCustomisationValue(
                      pageId,
                      "cardsSettings",
                      "cardCompletedProgressBarColor",
                    ) ?? ""
                  }
                  onChange={(color) =>
                    handleAttributeChange(
                      "cardCompletedProgressBarColor",
                      color,
                    )
                  }
                />
              </Box>
              <Box
                sx={{ width: "100%", height: "200px" }}
                component={"img"}
                src={completedProgress}
                alt="completed-progress"
              />
            </Box>
          </Box>
        </Stack>
      </Stack>
      {/* Content type colour */}
      <Stack spacing={1}>
        <Typography fontSize={"16px"} fontWeight="bold">
          Content type colour
        </Typography>
        <Box
          sx={{
            border: `1px solid ${gray[300]}`,
            borderRadius: "8px",
            px: 2,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              py: 2,
              gap: 2,
            }}
          >
            <Stack
              spacing={1}
              sx={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <Box>
                <Typography>Courses colour</Typography>
                <ColorPicker
                  value={
                    getCustomisationValue(
                      pageId,
                      "cardsSettings",
                      "cardCourseColor",
                    ) ?? ""
                  }
                  onChange={(color) =>
                    handleAttributeChange("cardCourseColor", color)
                  }
                />
              </Box>
              <Box>
                <Typography>Surveys colour</Typography>
                <ColorPicker
                  value={
                    getCustomisationValue(
                      pageId,
                      "cardsSettings",
                      "cardSurveyColor",
                    ) ?? ""
                  }
                  onChange={(color) =>
                    handleAttributeChange("cardSurveyColor", color)
                  }
                />
              </Box>
              <Box>
                <Typography>Policies colour</Typography>
                <ColorPicker
                  value={
                    getCustomisationValue(
                      pageId,
                      "cardsSettings",
                      "cardPolicyColor",
                    ) ?? ""
                  }
                  onChange={(color) =>
                    handleAttributeChange("cardPolicyColor", color)
                  }
                />
              </Box>
            </Stack>
            <Box
              sx={{ width: "100%", height: "200px" }}
              component={"img"}
              src={contentTypeColor}
              alt="content-type"
            />
          </Box>
        </Box>
      </Stack>
      {/* Overdue */}
      <Stack spacing={1}>
        <Typography fontSize={"16px"} fontWeight="bold">
          Overdue
        </Typography>
        <Box
          sx={{
            border: `1px solid ${gray[300]}`,
            borderRadius: "8px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            p: 2,
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <Box>
              <Typography>Text colour</Typography>
              <ColorPicker
                value={
                  getCustomisationValue(
                    pageId,
                    "cardsSettings",
                    "cardOverdueTextColor",
                  ) ?? ""
                }
                onChange={(color) =>
                  handleAttributeChange("cardOverdueTextColor", color)
                }
              />
            </Box>
          </Box>
          <Box
            sx={{
              backgroundColor: gray[200],
              borderRadius: "8px",
              height: 200,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                backgroundColor: error[50],
                color: error[700],
                borderRadius: "999px",
                border: `1px solid ${error[400]}`,
                padding: "2px 10px",
              }}
            >
              Overdue
            </Box>
          </Box>
        </Box>
      </Stack>
      {/* Date */}
      <Stack spacing={1}>
        <Typography fontSize={"16px"} fontWeight="bold">
          Date
        </Typography>
        <Box
          sx={{
            border: `1px solid ${gray[300]}`,
            borderRadius: "8px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            p: 2,
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <Box>
              <Typography>Text colour</Typography>
              <ColorPicker
                value={
                  getCustomisationValue(
                    pageId,
                    "cardsSettings",
                    "cardDateTextColor",
                  ) ?? ""
                }
                onChange={(color) =>
                  handleAttributeChange("cardDateTextColor", color)
                }
              />
            </Box>
          </Box>
          <Box
            sx={{
              backgroundColor: gray[200],
              borderRadius: "8px",
              height: 200,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                backgroundColor: brand[50],
                color: brand[700],
                borderRadius: "999px",
                border: `1px solid ${brand[400]}`,
                padding: "2px 10px",
              }}
            >
              {formattedDate}
            </Box>
          </Box>
        </Box>
      </Stack>
    </Stack>
  );
};
